// =====================================================
// LISTA DE EVALUACIONES CREDITICIAS
// src/pages/Agro/Evaluacion/ListaEvaluaciones.js
// =====================================================
// Corregido para usar el Layout de EWAN correctamente
// =====================================================

import React, { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Badge,
  Input,
  InputGroup,
  InputGroupText,
  Spinner,
} from "reactstrap"
import DataTable from "components/Tables/DataTable"
import { Link, useHistory } from "react-router-dom"
import dayjs from "dayjs"

import Layout from "components/Layout"
import { getEvaluaciones } from "api/agro"
import toastHandler from "toastHandler"

// Colores por estado
const estadoColors = {
  PENDIENTE: "warning",
  EN_REVISION: "info",
  APROBADA: "success",
  RECHAZADA: "danger",
  OBSERVADA: "secondary",
}

// Colores por score
const scoreColors = {
  A: "success",
  B: "info",
  C: "warning",
  D: "orange",
  E: "danger",
}

const ListaEvaluaciones = ({ permissionBy }) => {
  const history = useHistory()
  const { setError } = toastHandler()

  const [evaluaciones, setEvaluaciones] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtroEstado, setFiltroEstado] = useState("")
  const [busqueda, setBusqueda] = useState("")

  useEffect(() => {
    cargarEvaluaciones()
  }, [filtroEstado])

  const cargarEvaluaciones = () => {
    setLoading(true)
    getEvaluaciones({ estado: filtroEstado || undefined })
      .then(data => {
        setEvaluaciones(data || [])
      })
      .catch(err => {
        setError(err)
        setEvaluaciones([])
      })
      .finally(() => setLoading(false))
  }

  // Filtrar por búsqueda
  const evaluacionesFiltradas = evaluaciones.filter(e => {
    if (!busqueda) return true
    const search = busqueda.toLowerCase()
    return (
      e.productor?.razonSocial?.toLowerCase().includes(search) ||
      e.productor?.cuit?.includes(search) ||
      e.id?.toString().includes(search)
    )
  })

  // Columnas de la tabla
  const columns = [
    {
      name: "#",
      selector: row => row.id,
      sortable: true,
      width: "80px",
    },
    {
      name: "Fecha",
      selector: row => row.fechaSolicitud,
      sortable: true,
      width: "110px",
      cell: row =>
        row.fechaSolicitud
          ? dayjs(row.fechaSolicitud).format("DD/MM/YYYY")
          : "-",
    },
    {
      name: "Productor",
      selector: row => row.productor?.razonSocial,
      sortable: true,
      grow: 2,
      cell: row => (
        <div>
          <div className="fw-medium">{row.productor?.razonSocial || "-"}</div>
          <small className="text-muted">
            CUIT: {row.productor?.cuit || "-"}
          </small>
        </div>
      ),
    },
    {
      name: "Monto",
      selector: row => row.montoSolicitado,
      sortable: true,
      right: true,
      cell: row =>
        row.montoSolicitado
          ? `$ ${row.montoSolicitado.toLocaleString("es-AR")}`
          : "-",
    },
    {
      name: "Destino",
      selector: row => row.destino,
      width: "150px",
      cell: row => row.destino?.replace(/_/g, " ") || "-",
    },
    {
      name: "Score",
      selector: row => row.scoring?.categoriaRiesgo,
      sortable: true,
      width: "90px",
      center: true,
      cell: row =>
        row.scoring?.categoriaRiesgo ? (
          <Badge
            color={scoreColors[row.scoring.categoriaRiesgo] || "secondary"}
            style={{ fontSize: "14px", padding: "6px 12px" }}
          >
            {row.scoring.categoriaRiesgo}
          </Badge>
        ) : (
          <span className="text-muted">-</span>
        ),
    },
    {
      name: "Estado",
      selector: row => row.estado,
      sortable: true,
      width: "130px",
      cell: row => (
        <Badge color={estadoColors[row.estado] || "secondary"}>
          {row.estado?.replace(/_/g, " ") || "-"}
        </Badge>
      ),
    },
    {
      name: "Acciones",
      width: "120px",
      cell: row => (
        <Button
          size="sm"
          color="primary"
          onClick={() => history.push(`/agro/evaluaciones/${row.id}`)}
        >
          <i className="mdi mdi-eye me-1" />
          Ver
        </Button>
      ),
      ignoreRowClick: true,
    },
  ]

  // =====================================================
  // CONTENIDO PRINCIPAL
  // =====================================================
  const MainContent = () => (
    <Row>
      <Col xs={12}>
        <Card>
          <CardBody>
            {/* Header */}
            <Row className="mb-4">
              <Col md={6}>
                <h4 className="mb-0">
                  <i className="mdi mdi-clipboard-check me-2" />
                  Evaluaciones Crediticias
                </h4>
              </Col>
              <Col md={6} className="text-end">
                <Link to="/agro/evaluaciones/nueva">
                  <Button color="success">
                    <i className="mdi mdi-plus me-1" />
                    Nueva Solicitud
                  </Button>
                </Link>
              </Col>
            </Row>

            {/* Filtros */}
            <Row className="mb-3">
              <Col md={4}>
                <InputGroup>
                  <InputGroupText>
                    <i className="mdi mdi-magnify" />
                  </InputGroupText>
                  <Input
                    placeholder="Buscar por CUIT o razón social..."
                    value={busqueda}
                    onChange={e => setBusqueda(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Input
                  type="select"
                  value={filtroEstado}
                  onChange={e => setFiltroEstado(e.target.value)}
                >
                  <option value="">Todos los estados</option>
                  <option value="PENDIENTE">Pendientes</option>
                  <option value="EN_REVISION">En Revisión</option>
                  <option value="APROBADA">Aprobadas</option>
                  <option value="RECHAZADA">Rechazadas</option>
                  <option value="OBSERVADA">Observadas</option>
                </Input>
              </Col>
              <Col md={5} className="text-end">
                <Badge color="warning" className="me-2 p-2">
                  <i className="mdi mdi-clock me-1" />
                  Pendientes:{" "}
                  {evaluaciones.filter(e => e.estado === "PENDIENTE").length}
                </Badge>
                <Badge color="info" className="p-2">
                  Total: {evaluaciones.length}
                </Badge>
              </Col>
            </Row>

            {/* Tabla */}
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2">Cargando evaluaciones...</p>
              </div>
            ) : evaluacionesFiltradas.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i
                  className="mdi mdi-clipboard-text-off"
                  style={{ fontSize: 48 }}
                />
                <p className="mt-2">No hay evaluaciones para mostrar</p>
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={evaluacionesFiltradas}
                pagination
                paginationPerPage={15}
                paginationRowsPerPageOptions={[10, 15, 25, 50]}
                highlightOnHover
                pointerOnHover
                onRowClicked={row =>
                  history.push(`/agro/evaluaciones/${row.id}`)
                }
                striped
                responsive
              />
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  )

  // =====================================================
  // BREADCRUMB - Requerido por Layout EWAN
  // =====================================================
  const breadcrumbItems = [
    { title: "Agropecuario", link: "/agro" },
    { title: "Evaluaciones", link: "#" },
  ]

  return (
    <Layout
      components={[{ component: <MainContent /> }]}
      title="Evaluaciones Crediticias"
      breadcrumbItems={breadcrumbItems}
      permissionBy={permissionBy}
    />
  )
}

export default ListaEvaluaciones
